import React from 'react';
import {
  Modal,
  TextContainer,
  Button,
  Stack,
  Banner,
  ButtonGroup,
  TextField,
  Toast,
  Text
} from '@shopify/polaris';
import { SaveMinor } from '@shopify/polaris-icons';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

/**
 * Component for selecting which sections of a size chart to export or upload to Shopify
 */
export default class SizeChartExportOptions extends React.Component {
  constructor(props) {
    super(props);
    
    const initialOptions = this.props.initialOptions || {
      includeHeader: true,
      includeHeaderImage: true,
      includeFitGuideVisualizer: true,
      includeSizeTable: true,
      includeHowToMeasure: true,
      includeFitGuide: true,
      includeNotes: true,
      includeFooter: true,
      includeFooterImage: true,
      includeInfoText: true
    };
    
    this.state = {
      options: {...initialOptions},
      filename: props.chartName || 'sizechart',
      isSaving: false,
      showToast: false,
      toastMessage: '',
      toastError: false
    };
    
    // Bind methods
    this.toggleOption = this.toggleOption.bind(this);
    this.selectAll = this.selectAll.bind(this);
    this.deselectAll = this.deselectAll.bind(this);
    this.handleFilenameChange = this.handleFilenameChange.bind(this);
    this.saveSettings = this.saveSettings.bind(this);
    this.loadSavedSettings = this.loadSavedSettings.bind(this);
  }
  
  componentDidMount() {
    // Load saved settings when component mounts
    if (this.props.shopId && this.props.userId) {
      this.loadSavedSettings();
    }
  }
  
  componentDidUpdate(prevProps) {
    // Reset options when modal opens
    if (this.props.open && !prevProps.open) {
      // If we have saved settings, use those, otherwise use initialOptions
      if (this.state.savedSettings) {
        this.setState({
          options: {...this.state.savedSettings.options},
          filename: this.state.savedSettings.filename || this.props.chartName || 'sizechart'
        });
      } else {
        const initialOptions = this.props.initialOptions || {
          includeHeader: true,
          includeHeaderImage: true,
          includeFitGuideVisualizer: true,
          includeSizeTable: true,
          includeHowToMeasure: true,
          includeFitGuide: true,
          includeNotes: true,
          includeFooter: true,
          includeFooterImage: true,
          includeInfoText: true
        };
        
        this.setState({
          options: {...initialOptions},
          filename: this.props.chartName || 'sizechart'
        });
      }
    }
    
    // Load saved settings if shopId or userId changes
    if (this.props.shopId && this.props.userId && 
        (this.props.shopId !== prevProps.shopId || this.props.userId !== prevProps.userId)) {
      this.loadSavedSettings();
    }
  }
  
  // Load saved export settings from Firebase
  async loadSavedSettings() {
    try {
      console.log(`Loading saved export settings for shop ${this.props.shopId}, user ${this.props.userId}`);
      const settingsRef = doc(db, `shops/${this.props.shopId}/users/${this.props.userId}/preferences/exportOptions`);
      const docSnap = await getDoc(settingsRef);
      
      if (docSnap.exists()) {
        const savedSettings = docSnap.data();
        console.log('Loaded saved export settings:', savedSettings);
        
        this.setState({ savedSettings });
        
        // If modal is open, apply settings immediately
        if (this.props.open) {
          this.setState({
            options: {...savedSettings.options},
            filename: savedSettings.filename || this.props.chartName || 'sizechart'
          });
        }
      } else {
        console.log('No saved export settings found');
      }
    } catch (error) {
      console.error('Error loading saved export settings:', error);
    }
  }
  
  // Save current export settings to Firebase
  async saveSettings() {
    try {
      this.setState({ isSaving: true });
      
      const { options, filename } = this.state;
      
      const settingsData = {
        options,
        filename,
        lastUpdated: new Date().toISOString()
      };
      
      console.log('Saving export settings:', settingsData);
      
      // Save to Firebase
      const settingsRef = doc(db, `shops/${this.props.shopId}/users/${this.props.userId}/preferences/exportOptions`);
      const docSnap = await getDoc(settingsRef);
      
      if (docSnap.exists()) {
        await updateDoc(settingsRef, settingsData);
      } else {
        await setDoc(settingsRef, {
          ...settingsData,
          createdAt: new Date().toISOString()
        });
      }
      
      // Update saved settings state
      this.setState({ 
        savedSettings: settingsData,
        showToast: true,
        toastMessage: 'Export settings saved successfully',
        toastError: false
      });
      
      console.log('Export settings saved successfully');
    } catch (error) {
      console.error('Error saving export settings:', error);
      this.setState({
        showToast: true,
        toastMessage: 'Failed to save export settings',
        toastError: true
      });
    } finally {
      this.setState({ isSaving: false });
    }
  }
  
  // Toggle a specific option
  toggleOption(optionName) {
    this.setState(prevState => {
      const currentValue = prevState.options[optionName];
      const newValue = !currentValue;
      
      console.log(`Class-based toggle: ${optionName} from ${currentValue} to ${newValue}`);
      
      return {
        options: {
          ...prevState.options,
          [optionName]: newValue
        }
      };
    });
  }
  
  // Select all options
  selectAll() {
    const allSelected = {};
    Object.keys(this.state.options).forEach(key => {
      allSelected[key] = true;
    });
    
    this.setState({
      options: allSelected
    });
    
    console.log('Selected all options');
  }
  
  // Deselect all options except size table
  deselectAll() {
    const allDeselected = {};
    Object.keys(this.state.options).forEach(key => {
      allDeselected[key] = false;
    });
    
    // Keep at least size table selected to maintain validity
    allDeselected.includeSizeTable = true;
    
    this.setState({
      options: allDeselected
    });
    
    console.log('Deselected all options except size table');
  }

  // Handle filename change
  handleFilenameChange(value) {
    this.setState({ filename: value });
  }

  render() {
    const { open, onClose, onConfirm, title = 'Export Options', confirmText = 'Export', isLoading = false, shopId, userId } = this.props;
    const { options, filename, isSaving, showToast, toastMessage, toastError } = this.state;
    
    // Validate options - at least one section must be selected
    const isValid = Object.values(options).some(value => value === true);
    
    return (
      <>
        {showToast && (
          <Toast
            content={toastMessage}
            error={toastError}
            onDismiss={() => this.setState({ showToast: false })}
          />
        )}
        
        <Modal
          open={open}
          onClose={onClose}
          title={title}
          primaryAction={{
            content: confirmText,
            onAction: () => {
              console.log('Confirm button clicked with options:', options, 'filename:', filename);
              onConfirm(options, filename);
            },
            disabled: !isValid || isLoading,
            loading: isLoading
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: onClose
            }
          ]}
        >
          <Modal.Section>
            {!isValid && (
              <Banner status="warning" title="Selection Required">
                <p>Please select at least one section to include.</p>
              </Banner>
            )}
            
            <TextContainer>
              <div style={{ marginBottom: '20px' }}>
                <TextField
                  label="Filename"
                  value={filename}
                  onChange={this.handleFilenameChange}
                  helpText="Name for the exported file (without extension)"
                  autoComplete="off"
                />
              </div>
              
              {/* Save Settings Button - Only show if shopId and userId are provided */}
              {shopId && userId && (
                <div style={{ marginBottom: '20px' }}>
                  <Button
                    onClick={this.saveSettings}
                    disabled={isSaving}
                    loading={isSaving}
                    icon={SaveMinor}
                  >
                    Save Export Settings
                  </Button>
                  <div style={{ marginTop: '4px' }}>
                    <Text variant="bodySm" color="subdued">
                      Save these settings as your default export options
                    </Text>
                  </div>
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2>Select which sections to include:</h2>
                <ButtonGroup>
                  <Button onClick={this.selectAll}>Select All</Button>
                  <Button onClick={this.deselectAll}>Deselect All</Button>
                </ButtonGroup>
              </div>
            
            <Stack vertical>
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeHeader')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeHeader ? '#008060' : 'white'
                }}>
                  {options.includeHeader && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeHeader ? 'bold' : 'normal' }}>
                  Header Text
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeHeaderImage')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeHeaderImage ? '#008060' : 'white'
                }}>
                  {options.includeHeaderImage && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeHeaderImage ? 'bold' : 'normal' }}>
                  Header Image
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeFitGuideVisualizer')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeFitGuideVisualizer ? '#008060' : 'white'
                }}>
                  {options.includeFitGuideVisualizer && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeFitGuideVisualizer ? 'bold' : 'normal' }}>
                  Fit & Stretch Guide Visualizer
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeSizeTable')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeSizeTable ? '#008060' : 'white'
                }}>
                  {options.includeSizeTable && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeSizeTable ? 'bold' : 'normal' }}>
                  Size Chart Table
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeHowToMeasure')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeHowToMeasure ? '#008060' : 'white'
                }}>
                  {options.includeHowToMeasure && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeHowToMeasure ? 'bold' : 'normal' }}>
                  How to Measure Section
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeFitGuide')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeFitGuide ? '#008060' : 'white'
                }}>
                  {options.includeFitGuide && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeFitGuide ? 'bold' : 'normal' }}>
                  Fit Guide Information
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeNotes')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeNotes ? '#008060' : 'white'
                }}>
                  {options.includeNotes && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeNotes ? 'bold' : 'normal' }}>
                  Notes Section
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeFooter')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeFooter ? '#008060' : 'white'
                }}>
                  {options.includeFooter && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeFooter ? 'bold' : 'normal' }}>
                  Footer Text
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeFooterImage')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeFooterImage ? '#008060' : 'white'
                }}>
                  {options.includeFooterImage && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeFooterImage ? 'bold' : 'normal' }}>
                  Footer Image
                </span>
              </div>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <div 
                onClick={() => this.toggleOption('includeInfoText')}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '1px solid #999', 
                  borderRadius: '3px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: options.includeInfoText ? '#008060' : 'white'
                }}>
                  {options.includeInfoText && <span style={{ color: 'white' }}>✓</span>}
                </div>
                <span style={{ fontWeight: options.includeInfoText ? 'bold' : 'normal' }}>
                  Info Text
                </span>
              </div>
            </div>
          </Stack>
        </TextContainer>
      </Modal.Section>
      </Modal>
      </>
    );
  }
}
